"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ShieldCheck } from "lucide-react";
import { registerWalletAddressAction, revokeRegisteredAddressAction } from "@/app/actions";
import { CHAIN_META } from "@/components/chain";
import { Iso3CountryField } from "@/components/sender-info-form";

const CHAIN_CODES = Object.keys(CHAIN_META);

/** Pre-emptively whitelist a sender address so its deposits clear without a travel-rule hold. */
export function RegisterAddressForm({ customerId }: { customerId: string }) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const [f, setF] = useState({
    chain: "ethereum",
    address: "",
    label: "",
    type: "self_custody" as "self_custody" | "third_party",
    attested: false,
    entityType: "business" as "business" | "individual",
    legalName: "",
    country: "",
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    countryOfCitizenship: "",
  });
  function set<K extends keyof typeof f>(k: K, v: (typeof f)[K]) {
    setF((x) => ({ ...x, [k]: v }));
  }

  const originatorComplete =
    f.entityType === "business"
      ? !!f.legalName && !!f.country
      : !!f.firstName && !!f.lastName && !!f.dateOfBirth && !!f.countryOfCitizenship;
  const canSubmit = !!f.address && (f.type === "self_custody" ? f.attested : originatorComplete);

  function submit() {
    setError(null);
    setDone(null);
    const body =
      f.type === "self_custody"
        ? {
            type: "self_custody",
            chain: f.chain,
            address: f.address.trim(),
            selfCustodyAttestation: true,
            ...(f.label ? { label: f.label } : {}),
          }
        : {
            type: "third_party",
            chain: f.chain,
            address: f.address.trim(),
            originatorDetails:
              f.entityType === "business"
                ? { entityType: "business", legalName: f.legalName, country: f.country }
                : {
                    entityType: "individual",
                    firstName: f.firstName,
                    lastName: f.lastName,
                    dateOfBirth: f.dateOfBirth,
                    countryOfCitizenship: f.countryOfCitizenship,
                  },
            ...(f.label ? { label: f.label } : {}),
          };
    start(async () => {
      const res = await registerWalletAddressAction(customerId, body);
      if (res.ok) {
        setDone("Submitted — the address registers as soon as sanctions screening clears (often immediately).");
        setF((x) => ({ ...x, address: "", label: "", attested: false }));
        router.refresh();
      } else setError(res.error);
    });
  }

  return (
    <div className="card p-6">
      <div className="mb-1 flex items-center gap-2">
        <ShieldCheck size={18} />
        <h3 className="display text-[20px]">Register a sender address</h3>
      </div>
      <p className="mb-5 max-w-xl text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
        Crypto deposits from unknown addresses are held until the sender is identified (travel rule). Register the
        addresses you expect to receive from and their deposits clear straight through.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="label">Chain</label>
          <select className="field" value={f.chain} onChange={(e) => set("chain", e.target.value)}>
            {CHAIN_CODES.map((c) => (
              <option key={c} value={c}>
                {CHAIN_META[c].name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Label (optional)</label>
          <input className="field" value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Group treasury wallet" />
        </div>
        <div className="col-span-full">
          <label className="label">Address</label>
          <input className="field mono" placeholder="0x…" value={f.address} onChange={(e) => set("address", e.target.value)} />
        </div>

        <div className="col-span-full">
          <label className="label">Whose wallet is it?</label>
          <div className="flex gap-2">
            {(
              [
                ["self_custody", "ours (self-custody)"],
                ["third_party", "someone else's"],
              ] as const
            ).map(([t, label]) => (
              <button
                key={t}
                type="button"
                className="pill"
                style={
                  f.type === t
                    ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                    : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                }
                onClick={() => set("type", t)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {f.type === "self_custody" ? (
          <label className="col-span-full flex items-start gap-3 text-[13px] font-light">
            <input type="checkbox" className="mt-1" checked={f.attested} onChange={(e) => set("attested", e.target.checked)} />
            <span>
              We attest this wallet is owned and controlled by us — not by a third party or an exchange on our behalf.
            </span>
          </label>
        ) : (
          <>
            <div className="col-span-full">
              <label className="label">Owner is a…</label>
              <div className="flex gap-2">
                {(["business", "individual"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="pill"
                    style={
                      f.entityType === t
                        ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                        : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                    }
                    onClick={() => set("entityType", t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            {f.entityType === "business" ? (
              <>
                <div>
                  <label className="label">Legal name</label>
                  <input className="field" value={f.legalName} onChange={(e) => set("legalName", e.target.value)} />
                </div>
                <Iso3CountryField label="Country" value={f.country} onChange={(v) => set("country", v)} />
              </>
            ) : (
              <>
                <div>
                  <label className="label">First name</label>
                  <input className="field" value={f.firstName} onChange={(e) => set("firstName", e.target.value)} />
                </div>
                <div>
                  <label className="label">Last name</label>
                  <input className="field" value={f.lastName} onChange={(e) => set("lastName", e.target.value)} />
                </div>
                <div>
                  <label className="label">Date of birth</label>
                  <input className="field" type="date" value={f.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} />
                </div>
                <Iso3CountryField
                  label="Country of citizenship"
                  value={f.countryOfCitizenship}
                  onChange={(v) => set("countryOfCitizenship", v)}
                />
              </>
            )}
          </>
        )}
      </div>

      <button className="btn btn-primary mt-6" disabled={pending || !canSubmit} onClick={submit}>
        {pending ? "Registering…" : "Register address"}
      </button>

      {done && <div className="card-green mt-4 p-4 text-[14px] font-light">{done}</div>}
      {error && (
        <div className="card-cream mt-4 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
        </div>
      )}
    </div>
  );
}

export function RevokeAddressButton({ id }: { id: string }) {
  const router = useRouter();
  const [pending, start] = useTransition();
  return (
    <button
      className="btn btn-ghost !px-4 !py-1.5 text-[13px]"
      disabled={pending}
      onClick={() =>
        start(async () => {
          await revokeRegisteredAddressAction(id);
          router.refresh();
        })
      }
    >
      Revoke
    </button>
  );
}
